import { apiClient, isApiFetchError } from '@borradh-workspace/api-client';
import {
  type CreateDepositResponse,
  type DepositLinkQuote,
  type SendDepositLinkResponse,
  createDepositResponseSchema,
  depositLinkQuoteSchema,
  sendDepositLinkResponseSchema,
} from '@borradh-workspace/contracts';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { invalidateKeys, queryKeys } from '@/lib/query-keys';

export const depositLinkQuoteQueryOptions = (appointmentId: string) =>
  queryOptions({
    queryKey: queryKeys.deposits.link(appointmentId),
    queryFn: () =>
      apiClient.get<DepositLinkQuote>(
        `deposits/appointment/${encodeURIComponent(appointmentId)}/link`,
        { schema: depositLinkQuoteSchema }
      ),
    enabled: !!appointmentId,
    // Delivery / payment state moves on its own (webhooks, read receipts).
    // While a link is live the patient may be paying right now, so look
    // often enough that staff see the payment land within seconds. A bank
    // payment that is already clearing takes days, not seconds.
    refetchInterval: (query) =>
      query.state.data?.links.some(
        (l) => l.status === 'pending' && !l.paymentProcessingAt
      )
        ? 5_000
        : 30_000,
  });

export const useDepositLinkQuote = (appointmentId: string) =>
  useQuery(depositLinkQuoteQueryOptions(appointmentId));

/**
 * Keep the booking in step with its deposit link. A payment or a lapse moves
 * the booking server-side (held → confirmed, held → booked), so when the
 * newest link changes state, refresh the appointment queries the panels and
 * the calendar read — no reload needed.
 */
export const useRefreshBookingOnLinkChange = (
  quote: DepositLinkQuote | undefined
) => {
  const client = useQueryClient();
  const latest = quote?.links[0];
  const signature = latest ? `${latest.id}:${latest.status}` : '';
  const seen = useRef<string | null>(null);

  useEffect(() => {
    if (!quote) return;
    if (seen.current !== null && seen.current !== signature) {
      void invalidateKeys(client, queryKeys.appointments.all());
    }
    seen.current = signature;
  }, [client, quote, signature]);
};

/**
 * What staff read when the API fails SERVER-side. The global exception filter
 * masks every 5xx message to "An unexpected error occurred", so a Stripe
 * refusal reached staff as noise. 4xx messages are the API's own and pass
 * through.
 */
const SERVER_FAILURE_COPY = {
  create:
    'Stripe could not create the payment link. Nothing was charged; try again.',
  send: 'The payment link could not be sent right now. Try again.',
  revoke:
    'The payment link could not be revoked. It may still accept payment; try again.',
} as const;

export const depositLinkErrorMessage = (
  error: Error,
  action: keyof typeof SERVER_FAILURE_COPY,
  fallback: string
): string =>
  isApiFetchError(error) && error.status >= 500
    ? SERVER_FAILURE_COPY[action]
    : error.message || fallback;

const useInvalidateDeposits = () => {
  const client = useQueryClient();
  return () =>
    invalidateKeys(
      client,
      queryKeys.deposits.all(),
      queryKeys.appointments.all()
    );
};

/** Create a policy-priced link. The body carries NO amount — by contract. */
export const useCreateDepositLink = () => {
  const invalidate = useInvalidateDeposits();
  return useMutation({
    mutationFn: (appointmentId: string) =>
      apiClient.post<CreateDepositResponse>(
        'deposits',
        { appointmentId },
        { schema: createDepositResponseSchema }
      ),
    onSuccess: (res) => {
      invalidate();
      toast.success(
        res.status === 'created'
          ? 'Payment link created'
          : 'No deposit is due for this booking'
      );
    },
    onError: (error: Error) => {
      // A Stripe refusal is recorded as a failed attempt — show it now.
      invalidate();
      toast.error(
        depositLinkErrorMessage(
          error,
          'create',
          'Could not create the payment link'
        )
      );
    },
  });
};

export const useSendDepositLink = () => {
  const invalidate = useInvalidateDeposits();
  return useMutation({
    mutationFn: (depositId: string) =>
      apiClient.post<SendDepositLinkResponse>(
        `deposits/${encodeURIComponent(depositId)}/send`,
        {},
        { schema: sendDepositLinkResponseSchema }
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Payment link sent to the patient');
    },
    onError: (error: Error) => {
      // A 409 means the message went out but the link changed meanwhile —
      // refresh so the panel shows where it stands now.
      invalidate();
      toast.error(
        depositLinkErrorMessage(
          error,
          'send',
          'Could not send the payment link'
        )
      );
    },
  });
};

export const useRevokeDepositLink = () => {
  const invalidate = useInvalidateDeposits();
  return useMutation({
    mutationFn: (depositId: string) =>
      apiClient.post(`deposits/${encodeURIComponent(depositId)}/revoke`, {}),
    onSuccess: () => {
      invalidate();
      toast.success('Payment link revoked');
    },
    onError: (error: Error) => {
      invalidate();
      toast.error(
        depositLinkErrorMessage(
          error,
          'revoke',
          'Could not revoke the payment link'
        )
      );
    },
  });
};
